import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Link } from "react-router-dom";

/** Confirmación + resultado de "Crear todas las rutas". */
export default function CrearTodasDialog({ open, onOpenChange, resumen, onConfirm, resultado, loading, onVolver }) {
  const [estado, setEstado] = useState("borrador");
  const [prefijo, setPrefijo] = useState("Reparto");
  if (!resumen) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="crear-todas-dialog">
        <DialogHeader><DialogTitle>{resultado ? "Resultado de la creación" : "Crear todas las rutas de la propuesta"}</DialogTitle></DialogHeader>
        {!resultado ? (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-zinc-900 rounded-lg p-2"><div className="text-xl font-bold text-amber-300" data-testid="ct-n-rutas">{resumen.rutas.length}</div><div className="text-[10px] uppercase text-zinc-500">Rutas</div></div>
              <div className="bg-zinc-900 rounded-lg p-2"><div className="text-xl font-bold text-emerald-300">{resumen.totalParadas}</div><div className="text-[10px] uppercase text-zinc-500">Paradas</div></div>
              <div className="bg-zinc-900 rounded-lg p-2"><div className="text-xl font-bold text-zinc-300">{resumen.excluidas}</div><div className="text-[10px] uppercase text-zinc-500">Excluidas (fuera)</div></div>
            </div>
            <div className="text-xs text-zinc-400">📅 Fecha de planificación: <b className="text-zinc-200">{resumen.fecha}</b></div>
            <ul className="text-xs divide-y divide-zinc-800 border border-zinc-800 rounded-lg" data-testid="ct-lista">
              {resumen.rutas.map((r) => (
                <li key={r.id} className="flex items-center gap-2 px-3 py-1.5">
                  <span className="font-bold w-8">{r.id}</span><span className="flex-1 truncate">{r.zona || "—"}</span>
                  <span className="font-mono">{r.n} paradas</span>
                  <span className={`w-28 truncate text-right ${r.montador ? "text-cyan-300" : "text-zinc-500 italic"}`}>{r.montador || "sin montador"}</span>
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-zinc-500">Prefijo del nombre</Label>
                <Input value={prefijo} onChange={(e) => setPrefijo(e.target.value)} data-testid="ct-prefijo" className="h-9 bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500">Estado inicial</Label>
                <div className="flex gap-1 mt-1">
                  {["borrador", "planificada"].map((e) => (
                    <button key={e} type="button" onClick={() => setEstado(e)} data-testid={`ct-estado-${e}`}
                      className={`flex-1 h-9 rounded text-xs border ${estado === e ? "bg-amber-500/20 border-amber-500/60 text-amber-100" : "border-zinc-800 text-zinc-400"}`}>{e === "borrador" ? "Borrador (nadie avisado)" : "Planificada"}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-[11px] text-zinc-500">Se usa el mismo flujo que crear una ruta individual, una a una. Si alguna falla se indica exactamente cuál. Las excluidas siguen pendientes en la app.</div>
          </div>
        ) : (
          <ul className="text-sm space-y-1.5" data-testid="ct-resultados">
            {resultado.resultados.map((r) => (
              <li key={r.id} className="flex items-center gap-2 bg-zinc-900 rounded-lg px-3 py-2">
                {r.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <XCircle className="w-4 h-4 text-rose-400 shrink-0" />}
                <span className="font-bold w-8">{r.id}</span>
                {r.ok ? (
                  <Link to={`/rutas/${r.ruta_id}`} className="text-cyan-300 hover:underline flex-1" data-testid={`ct-ok-${r.id}`}>{r.codigo} · {r.n_paradas} paradas</Link>
                ) : <span className="text-rose-300 text-xs flex-1" data-testid={`ct-err-${r.id}`}>{r.error}{r.code === "en_ruta" && r.lineas?.length ? <span className="block text-zinc-400">{r.lineas.map((l) => `${(l.linea_id || l).slice(0, 8)}… → ${l.ruta || "otra ruta"}`).join(" · ")}</span> : null}</span>}
              </li>
            ))}
            <li className="text-xs text-zinc-400 pt-1">✅ {resultado.creadas} creada(s) · ❌ {resultado.fallidas} fallida(s)</li>
          </ul>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>{resultado ? "Cerrar" : "Cancelar"}</Button>
          {resultado && onVolver && (
            <Button onClick={onVolver} data-testid="ct-volver-calendario" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">Volver al calendario</Button>
          )}
          {!resultado && (
            <Button onClick={() => onConfirm({ estado, prefijo })} disabled={loading || resumen.rutas.length === 0} data-testid="ct-confirmar"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Crear {resumen.rutas.length} ruta(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
